import { msg, Trans } from "@lingui/macro"
import { useLingui } from "@lingui/react"
import {
    Box,
    Button,
    Chip,
    Slide,
    Snackbar,
    Stack,
    styled,
    Typography,
    useTheme
} from '@mui/material'
import {
    GeolocateControl,
    Layer,
    Map,
    Marker,
    NavigationControl,
    Popup as PopupComponent,
    ScaleControl,
    Source,
    useMap
} from '@vis.gl/react-maplibre'
import { Popup } from 'maplibre-gl'
import 'maplibre-gl/dist/maplibre-gl.css'
import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { ThemeContext } from '../../App'
import { CrashMapDetailPanel } from './CrashMapDetailPanel'
import { CommunityMapLayer } from './community-map-layer.component'
import { CommunityMapLayerClickHandler } from './community-map-layer-click-handler'
import { CommunityMapLayerFocusHandler } from './community-map-layer-focus-handler'
import { CommunityMapLayerPopup } from './community-map-layer-popup.component'
import { buildCommunitySwitchesKey } from './crash-map-detail-utils'
import { NewApiBaseUrl } from '../../consts/new-api-base-url'
import { DESKTOP_HISTORY_DRAWER_WIDTH_PX } from '../../consts/form-drawer-width'
import { SEVERITIES } from '../../constants'
import useHttpApiNew from '../../hooks/useHttpApiNew'
import useWindowSize from '../../hooks/useWindowSize'
import { useDesktopSidebar } from '../../contexts/desktop-sidebar-context'
import CommunityMapButton from "../buttons/CommunityMapButton"
import LayerMapButton from "../buttons/LayerMapButton"
import Copyright from '../Copyright'
import GeneralInfo from '../GeneralInfo'
import ModalVisMarkerInfo from '../modals/map/ModalVisMarkerinfo'
import { heatmapLayer } from './heatMapLayer'
import { buildRoadRiskMapLayer, roadRiskMapHitLayer, roadNetworkUnderlayLayer, roadNetworkUnderlayHitLayer } from './roadRiskMapLayer'
import RoadRiskMapLegend from './RoadRiskMapLegend'
import RoadRiskMapToolbar, { RoadRiskMapToolbarToggle } from './RoadRiskMapToolbar'
import MapYearFilterToolbar from './MapYearFilterToolbar'
import { RISK_MAP_FOCUS_ZOOM, formatRiskScore } from '../../shared/constants/risk-map'
import { CustomPin } from './Pin'
import PolygonDrawerButton from './PolygonDrawerButton'
import PolygonStatsResults from "./PolygonStatsResults"
import PolygonStatsBackdrop from './PolygonStatsBackdrop'
import StylesControl from './StylesControl'
import {
    buildStatsRequestPayload,
    chartFieldExistsInStatsConfig,
    extractChartRowsFromStatsResponse,
    normalizeStatsRowsToChartData,
    resolveStatsChartFieldName,
} from '../data-analysis/charts/stats-response-utils'
import {
    getDashboardColorMapForChartType,
    getRandomColors,
} from '../data-analysis/charts/dashboard-random-palette'
import { statisticsMapGradientColors } from '../data-analysis/charts/statistics-chart-palette'
import { enrichStatsRequestPayload } from '../../pages/Statistics/statistics-dataset-context'
import { STATS_REPORT_TYPE_MERGED } from '../../pages/Statistics/statistics-report-type'

const CENTER = [
    Number.parseFloat(process.env.REACT_APP_CENTER_LON)
    , Number.parseFloat(process.env.REACT_APP_CENTER_LAT)
] // LON, LAT Rome
const ZOOM = 10
/** Supercluster / GeoJSON: max zoom level at which clustering applies (MapLibre `clusterMaxZoom`). */
const CRASH_CLUSTER_MAX_ZOOM = 11
/** Soglia pin DOM: stessa del motore (zoom > clusterMaxZoom). Cerchi WebGL coprono il ritardo di React. */
const shouldShowCrashPins = mapZoom =>
    Number.isFinite(mapZoom) && mapZoom > CRASH_CLUSTER_MAX_ZOOM
/** Zoom minimo dopo click cluster: sempre oltre la soglia uncluster + epsilon (evita easeTo che ferma a 11.0). */
const CLUSTER_CLICK_ZOOM_MIN = CRASH_CLUSTER_MAX_ZOOM + 0.06
/** Passo massimo di zoom per ogni click sul cluster (evita un solo salto enorme verso expansionZoom). */
const CLUSTER_CLICK_ZOOM_STEP = 2
const CLUSTER_EASE_MS_MIN = 600
const CLUSTER_EASE_MS_MAX = 2000

const clusterEaseDurationMs = (z0, z1) => {
    const delta = Math.abs(z1 - z0)
    return Math.round(
        Math.min(
            CLUSTER_EASE_MS_MAX,
            Math.max(CLUSTER_EASE_MS_MIN, 380 + delta * 320)
        )
    )
}
/** Colori severity (allineati a SEVERITIES / MUI) per layer punti unclustered. */
const UNCLUSTERED_SEVERITY_CIRCLE_COLOR = [
    'match',
    ['get', 'severity'],
    0, '#616161',
    1, '#00E676',
    2, '#FFD600',
    3, '#EF6C00',
    4, '#D50000',
    5, '#7C96FD',
    6, '#470080',
    '#616161'
]

const PINSCALE = 0.75
const OFFSET = [0, -10]
const SPEED = 0.6
const STATS_DRAWER_WIDTH = 720
/**
 * Bordo inferiore del gruppo custom (asse Y dal basso della mappa).
 * Allineato allo stack nativo: zoom (3 btn) + 8px + geolocate + 8px — stesso 8px di `.maplibregl-ctrl-bottom-right .maplibregl-ctrl-group`.
 */
const MAP_CUSTOM_TOOLBAR_BOTTOM_PX = 140
const getCrashesInPolygonUrl = new URL(`${NewApiBaseUrl}/Stats/crashes-in-polygon`)

// Show cross on selecting position
const NewCrashSelector = ({ selectorTop, selectorLeft, navDrawerWidthPx }) => {
    const windowSize = useWindowSize()

    const HLine = styled(Box)(({ theme }) => ({
        width: windowSize.width - navDrawerWidthPx,
        height: 1,
        top: selectorTop,
        position: 'absolute',
        backgroundColor: '#000',
        pointerEvents: 'none',
        zIndex: theme.zIndex.drawer - 1
    }))

    const VLine = styled(Box)(({ theme }) => ({
        width: 1,
        height: windowSize.height,
        left: selectorLeft,
        position: 'absolute',
        backgroundColor: '#000',
        pointerEvents: 'none',
        zIndex: theme.zIndex.drawer - 1
    }))

    return (
        <>
            <HLine />
            <VLine />
        </>
    )
}

/**
 * Sincronizza `mapZoom` con `map.getZoom()` reale.
 * `render` a ogni frame durante easeTo; niente dedup manuale (React ignora setState se il numero è uguale).
 */
function CrashMapZoomSync({ setMapZoom }) {
    const { current: map } = useMap()

    useEffect(() => {
        if (!map?.on || !map?.getZoom) return undefined

        const sync = () => setMapZoom(map.getZoom())

        map.on('zoom', sync)
        map.on('moveend', sync)
        map.on('zoomend', sync)
        map.on('render', sync)
        sync()

        return () => {
            map.off('zoom', sync)
            map.off('moveend', sync)
            map.off('zoomend', sync)
            map.off('render', sync)
        }
    }, [map, setMapZoom])

    return null
}

/** Reports current map bounds + zoom (WGS84) to parent for risk-map refetch. */
function MapBoundsReporter({ onMapBoundsChange }) {
    const { current: map } = useMap()

    useEffect(() => {
        if (!map?.on || !map?.getBounds || typeof onMapBoundsChange !== 'function') return undefined

        const report = () => {
            const b = map.getBounds()
            if (!b) return
            onMapBoundsChange({
                lat0: b.getSouth(),
                lat1: b.getNorth(),
                lon0: b.getWest(),
                lon1: b.getEast(),
                zoom: map.getZoom?.() ?? undefined,
            })
        }

        map.on('moveend', report)
        map.on('zoomend', report)
        report()

        return () => {
            map.off('moveend', report)
            map.off('zoomend', report)
        }
    }, [map, onMapBoundsChange])

    return null
}

function RoadRiskClickHandler({ visible, onFeatureClick }) {
    const { current: map } = useMap()

    useEffect(() => {
        if (!map?.on || !visible) return undefined

        const onClick = (e) => {
            const feature = e.features?.[0]
            if (!feature) return
            e.originalEvent?.stopPropagation()
            onFeatureClick?.({
                longitude: e.lngLat.lng,
                latitude: e.lngLat.lat,
                properties: feature.properties || {},
                kind: 'risk',
            })
        }

        const onEnter = () => {
            map.getCanvas().style.cursor = 'pointer'
        }
        const onLeave = () => {
            map.getCanvas().style.cursor = ''
        }

        map.on('click', 'road-risk-map-hit', onClick)
        map.on('mouseenter', 'road-risk-map-hit', onEnter)
        map.on('mouseleave', 'road-risk-map-hit', onLeave)

        return () => {
            map.off('click', 'road-risk-map-hit', onClick)
            map.off('mouseenter', 'road-risk-map-hit', onEnter)
            map.off('mouseleave', 'road-risk-map-hit', onLeave)
        }
    }, [map, visible, onFeatureClick])

    return null
}

function RoadNetworkUnderlayClickHandler({ visible, onFeatureClick }) {
    const { current: map } = useMap()

    useEffect(() => {
        if (!map?.on || !visible) return undefined

        const onClick = (e) => {
            // Prefer scored risk features when both layers are hit.
            try {
                if (map.getLayer?.('road-risk-map-hit')) {
                    const riskHits = map.queryRenderedFeatures(e.point, { layers: ['road-risk-map-hit'] })
                    if (riskHits?.length) return
                }
            } catch {
                /* layer may not be ready */
            }

            const feature = e.features?.[0]
            if (!feature) return
            e.originalEvent?.stopPropagation()
            onFeatureClick?.({
                longitude: e.lngLat.lng,
                latitude: e.lngLat.lat,
                properties: feature.properties || {},
                kind: 'network',
            })
        }

        const onEnter = () => {
            map.getCanvas().style.cursor = 'pointer'
        }
        const onLeave = () => {
            map.getCanvas().style.cursor = ''
        }

        map.on('click', 'road-network-underlay-hit', onClick)
        map.on('mouseenter', 'road-network-underlay-hit', onEnter)
        map.on('mouseleave', 'road-network-underlay-hit', onLeave)

        return () => {
            map.off('click', 'road-network-underlay-hit', onClick)
            map.off('mouseenter', 'road-network-underlay-hit', onEnter)
            map.off('mouseleave', 'road-network-underlay-hit', onLeave)
        }
    }, [map, visible, onFeatureClick])

    return null
}

export default function MapLibre({
    isMobile,
    canDrawPolygon = false,
    isPositioning,
    isFormOpen,
    isHistoryOpen,
    onStatsPanelOpen,
    onStatsPanelClose,
    onDrawingStart,
    onDrawingEnd,
    showCrashes,
    showPublicCrashes,
    isMerged = false,
    isUnmerged = false,
    allCrashes,
    publicCrashes,
    crashesSummary,
    heatMapData,
    roadRiskMapData,
    roadRiskMapMetadata,
    roadRiskMapPrevScores,
    roadNetworkMapData,
    roadNetworkUnderlayEnabled,
    onRoadNetworkUnderlayEnabledChange,
    riskMapLoading,
    riskMapYear,
    riskMapYears,
    mapFilterYears = [],
    onMapFilterYearsChange,
    onRiskMapRefresh,
    onRiskMapDemoSeed,
    riskMapDemoSeedLoading,
    heatmapLayerOn = false,
    onHeatmapLayerOnChange,
    riskMapLayerOn = false,
    onRiskMapLayerOnChange,
    onMapBoundsChange,
    newCrash,
    getCrash,
    layersVisibility,
    handlePositioningState,
    handleIsPositionSetState,
    handleCoordinates,
    handleFormOpen,
    handleSetNewCrash,
    handleCrashToEdit,
    handleOwnPosCoords,
    showSafetyIssues,
    safetyIssuesList,
    layersList,
    setLayersList,
    canReorderMapLayers,
    onMoveLayerUp,
    onMoveLayerDown,
    isMapLayerReordering,
    mapRef,
    communitySwitches,
    setCommunitySwitches,
    isPositionSet,
    shouldTriggerGeoControl
}) {
    const mapKey = process.env.REACT_APP_API_KEY
    const theme = useTheme()
    const barHeight = theme.mixins.toolbar.minHeight
    const { effectiveWidthPx: navDrawerWidthPx } = useDesktopSidebar()
    const formDrawerWidth = theme.mixins.formDrawerWidth
    const location = useLocation()
    const { mode, toggleTheme } = useContext(ThemeContext)
    const state = location.state
    const { init, token = null } = state || {}
    const user = useMemo(() => init?.user, [init])
    const organizations = useMemo(() => init?.organizations, [init])
    const [mapStyle, setMapStyle] = useState('openstreetmap')
    const geoControlRef = useRef()
    const { _ } = useLingui()
    const { request } = useHttpApiNew()

    const [selectorTop, setSelectorTop] = useState(0)
    const [selectorLeft, setSelectorLeft] = useState(0)
    const transitionDuration = {
        enter: theme.transitions.duration.enteringScreen,
        exit: theme.transitions.duration.leavingScreen
    }
    const [popupInfo, setPopupInfo] = useState(null)
    const [popupPublicCrashInfo, setPopupPublicCrashInfo] = useState(null)
    const [popupSafetyIssueInfo, setPopupSafetyIssueInfo] = useState(null)
    const [modalinfo, setModalInfo] = useState(null)
    const [layerPopupInfo, setLayerPopupInfo] = useState(null)
    const [riskMapPopupInfo, setRiskMapPopupInfo] = useState(null)
    const focusMapLayerRef = useRef(null)

    const [mapZoom, setMapZoom] = useState(ZOOM) // <-- Added state for zoom
    const [polygonSnackbarOpen, setPolygonSnackbarOpen] = useState(false)
    const [polygonStatsLoading, setPolygonStatsLoading] = useState(false)
    const [statsPanelOpen, setStatsPanelOpen] = useState(false)
    const [polygonStatsData, setPolygonStatsData] = useState(null)
    const [polygonStatsDataSecond, setPolygonStatsDataSecond] = useState(null)
    const [selectedSecondChartOption, setSelectedSecondChartOption] = useState(null)
    const [polygonChartTypes, setPolygonChartTypes] = useState({ 0: 'bar', 1: 'pie' })
    const [analysisPanelOpen, setAnalysisPanelOpen] = useState(false)
    const analysisToolsAvailable =
        layersVisibility?.analysis === 'visible' || layersVisibility?.riskMap === 'visible'

    useEffect(() => {
        if (analysisToolsAvailable) {
            setAnalysisPanelOpen(true)
        } else {
            setAnalysisPanelOpen(false)
        }
    }, [analysisToolsAvailable])
    const polygonMainOrdinalColors = useMemo(() => getRandomColors(14), [])
    const polygonSecondOrdinalColors = useMemo(() => getRandomColors(14), [])
    const lastPolygonPointsRef = useRef(null)

    const handleSetStyle = style => setMapStyle(style)

    const clearPopups = () => {
        setPopupInfo(null)
        setPopupPublicCrashInfo(null)
        setPopupSafetyIssueInfo(null)
        setLayerPopupInfo(null)
        setRiskMapPopupInfo(null)
    }

    const roadRiskLayerStyle = useMemo(
        () => buildRoadRiskMapLayer(roadRiskMapMetadata?.maxRiskScore),
        [roadRiskMapMetadata?.maxRiskScore]
    )

    const handleRoadRiskFeatureClick = useCallback((info) => {
        setPopupInfo(null)
        setPopupPublicCrashInfo(null)
        setPopupSafetyIssueInfo(null)
        setLayerPopupInfo(null)
        setRiskMapPopupInfo(info)
    }, [])

    const handleRoadNetworkFeatureClick = useCallback((info) => {
        setPopupInfo(null)
        setPopupPublicCrashInfo(null)
        setPopupSafetyIssueInfo(null)
        setLayerPopupInfo(null)
        setRiskMapPopupInfo(info)
    }, [])

    const handleCommunityLayerFeatureClick = useCallback((info) => {
        setPopupInfo(null)
        setPopupPublicCrashInfo(null)
        setPopupSafetyIssueInfo(null)
        setRiskMapPopupInfo(null)
        setLayerPopupInfo(info)
    }, [])

    const handleFocusRiskSegmentFromTopTen = useCallback((feature) => {
        if (!feature) return

        let longitude = Number(feature?.properties?.centerLon)
        let latitude = Number(feature?.properties?.centerLat)
        if (!Number.isFinite(longitude) || !Number.isFinite(latitude)) {
            const coords = []
            const walk = (node) => {
                if (!Array.isArray(node)) return
                if (typeof node[0] === 'number' && typeof node[1] === 'number') {
                    coords.push([node[0], node[1]])
                    return
                }
                node.forEach(walk)
            }
            walk(feature?.geometry?.coordinates)
            if (coords.length === 0) return
            longitude = coords.reduce((sum, c) => sum + c[0], 0) / coords.length
            latitude = coords.reduce((sum, c) => sum + c[1], 0) / coords.length
        }

        setPopupInfo(null)
        setPopupPublicCrashInfo(null)
        setPopupSafetyIssueInfo(null)
        setLayerPopupInfo(null)
        setRiskMapPopupInfo({
            longitude,
            latitude,
            properties: feature.properties || {},
            kind: 'risk',
        })

        const map = mapRef?.current
        if (map?.flyTo) {
            map.flyTo({
                center: [longitude, latitude],
                zoom: RISK_MAP_FOCUS_ZOOM,
                speed: 1.4,
                curve: 1.2,
            })
        }
    }, [mapRef])

    const communitySwitchesKey = useMemo(
        () => buildCommunitySwitchesKey(communitySwitches),
        [communitySwitches],
    )

    useEffect(() => {
        clearPopups()
    }, [
        showCrashes,
        showPublicCrashes,
        showSafetyIssues,
        isMerged,
        isUnmerged,
        mapFilterYears,
        heatmapLayerOn,
        riskMapLayerOn,
        roadNetworkUnderlayEnabled,
        layersVisibility?.analysis,
        layersVisibility?.heatmap,
        layersVisibility?.riskMap,
        communitySwitchesKey,
    ])

    useEffect(() => {
        if (!layerPopupInfo) return
        const isLayerStillOpen = layersList.some(
            (layer) => layer.isOpen && layer.id === layerPopupInfo.layerId,
        )
        if (!isLayerStillOpen) setLayerPopupInfo(null)
    }, [layersList, layerPopupInfo])

    /*
    const FlyToCrash = () => {
        const { current: map } = useMap()
        
        useEffect(() => {
            if (!map) return
            if (!newCrash) return
            // Only fly to existing crashes (not new ones being created)
            if (newCrash.isNew) return
            // Check if crash has valid coordinates
            if (newCrash.Latitude && newCrash.Longitude) {
                // Add a small delay to ensure the marker is rendered
                setTimeout(() => {
                    map.flyTo({
                        center: [newCrash.Longitude, newCrash.Latitude],
                        zoom: 15,
                        speed: 7,
                        curve: 1,
                        easing(t) { return t; }
                    })
                }, 100)
            }
        }, [newCrash])
        
        return null
    }
        */

    // -----------------------------
    // CLUSTER: build geojson from allCrashes
    // -----------------------------

    const ClusterClickHandler = ({ mapRef }) => {
        const { current: map } = useMap()

        useEffect(() => {
            if (!map || !showCrashes || !crashesGeoJSON?.features?.length) return

            const handleClusterClick = async (e) => {
                e.originalEvent?.stopPropagation()

                const features = map.queryRenderedFeatures(e.point, {
                    layers: ['cluster-click', 'cluster-layer']
                })

                if (features.length === 0) return

                const clusterFeature = features.find(f => f.properties?.cluster_id !== undefined)
                if (!clusterFeature) return

                const coordinates = clusterFeature.geometry.coordinates
                const clusterId = clusterFeature.properties?.cluster_id
                const source = map.getSource('crash-clusters')

                if (
                    clusterId !== undefined
                    && source
                    && typeof source.getClusterExpansionZoom === 'function'
                ) {
                    try {
                        const expansionZoom = await source.getClusterExpansionZoom(clusterId)
                        const desiredEnd = Math.min(
                            Math.max(expansionZoom + 0.5, CLUSTER_CLICK_ZOOM_MIN),
                            22
                        )
                        const currentZ = map.getZoom()
                        if (desiredEnd <= currentZ + 1e-4) return
                        const targetZoom = Math.min(
                            currentZ + CLUSTER_CLICK_ZOOM_STEP,
                            desiredEnd
                        )
                        map.easeTo({
                            center: coordinates,
                            zoom: targetZoom,
                            duration: clusterEaseDurationMs(currentZ, targetZoom)
                        })
                        return
                    } catch {
                        // fallback sotto
                    }
                }

                const z = map.getZoom()
                const next = z < 7 ? z + 4 : z + 2
                const fallbackTarget = Math.min(Math.max(next, CLUSTER_CLICK_ZOOM_MIN), 16)
                map.easeTo({
                    center: coordinates,
                    zoom: fallbackTarget,
                    duration: clusterEaseDurationMs(z, fallbackTarget)
                })
            }

            // Change cursor on hover (cluster bubbles only; crash pins use React Marker onClick)
            const handleMouseEnter = () => {
                map.getCanvas().style.cursor = 'pointer'
            }
            const handleMouseLeave = () => {
                map.getCanvas().style.cursor = ''
            }

            const setupHandlers = () => {
                try {
                    const hasCluster =
                        map.getLayer('cluster-click') || map.getLayer('cluster-layer')
                    if (hasCluster) {
                        map.off('click', 'cluster-click', handleClusterClick)
                        map.off('click', 'cluster-layer', handleClusterClick)
                        map.off('mouseenter', 'cluster-click', handleMouseEnter)
                        map.off('mouseenter', 'cluster-layer', handleMouseEnter)
                        map.off('mouseleave', 'cluster-click', handleMouseLeave)
                        map.off('mouseleave', 'cluster-layer', handleMouseLeave)

                        map.on('click', 'cluster-click', handleClusterClick)
                        map.on('click', 'cluster-layer', handleClusterClick)
                        map.on('mouseenter', 'cluster-click', handleMouseEnter)
                        map.on('mouseenter', 'cluster-layer', handleMouseEnter)
                        map.on('mouseleave', 'cluster-click', handleMouseLeave)
                        map.on('mouseleave', 'cluster-layer', handleMouseLeave)
                        return true
                    }
                } catch (e) {
                    // Layer might not exist yet
                }
                return false
            }

            // Try to setup handlers
            let handlersSetup = setupHandlers()
            const timeouts = []

            // If layer not ready, try multiple times
            if (!handlersSetup) {
                [100, 300, 500, 1000, 2000].forEach(delay => {
                    const timeoutId = setTimeout(() => {
                        if (!handlersSetup) {
                            handlersSetup = setupHandlers()
                        }
                    }, delay)
                    timeouts.push(timeoutId)
                })
            }

            // Also try when map is idle or loaded
            const handleIdle = () => {
                if (!handlersSetup) {
                    handlersSetup = setupHandlers()
                }
            }
            map.on('idle', handleIdle)

            // Try when map is loaded
            let loadHandler = null
            if (map.loaded()) {
                if (!handlersSetup) {
                    handlersSetup = setupHandlers()
                }
            } else {
                loadHandler = () => {
                    if (!handlersSetup) {
                        handlersSetup = setupHandlers()
                    }
                }
                map.once('load', loadHandler)
            }

            // Also try when source data is loaded
            const source = map.getSource('crash-clusters')
            if (source) {
                const handleData = () => {
                    if (!handlersSetup) {
                        handlersSetup = setupHandlers()
                    }
                }
                source.on('data', handleData)

                return () => {
                    timeouts.forEach(clearTimeout)
                    map.off('click', 'cluster-click', handleClusterClick)
                    map.off('click', 'cluster-layer', handleClusterClick)
                    map.off('mouseenter', 'cluster-click', handleMouseEnter)
                    map.off('mouseenter', 'cluster-layer', handleMouseEnter)
                    map.off('mouseleave', 'cluster-click', handleMouseLeave)
                    map.off('mouseleave', 'cluster-layer', handleMouseLeave)
                    map.off('idle', handleIdle)
                    if (loadHandler) {
                        map.off('load', loadHandler)
                    }
                    source.off('data', handleData)
                }
            }

            return () => {
                timeouts.forEach(clearTimeout)
                map.off('click', 'cluster-click', handleClusterClick)
                map.off('click', 'cluster-layer', handleClusterClick)
                map.off('mouseenter', 'cluster-click', handleMouseEnter)
                map.off('mouseenter', 'cluster-layer', handleMouseEnter)
                map.off('mouseleave', 'cluster-click', handleMouseLeave)
                map.off('mouseleave', 'cluster-layer', handleMouseLeave)
                map.off('idle', handleIdle)
                if (loadHandler) {
                    map.off('load', loadHandler)
                }
            }
        }, [map, showCrashes, crashesGeoJSON])

        return null
    }

    const crashesGeoJSON = useMemo(() => {
        return {
            type: "FeatureCollection",
            features: (allCrashes?.reduced || []).map(crash => ({
                type: "Feature",
                properties: {
                    crashId: crash['Crash id'],
                    severity: crash['Crash severity'],
                },
                geometry: {
                    type: "Point",
                    coordinates: [Number(crash.Longitude), Number(crash.Latitude)]
                }
            }))
        }
    }, [allCrashes])

    const allCrashesMarkers = useMemo(
        () =>
            allCrashes?.reduced
                ?.map(crash => {
                    const longitude = Number(crash.Longitude)
                    const latitude = Number(crash.Latitude)
                    if (
                        !Number.isFinite(longitude)
                        || !Number.isFinite(latitude)
                        || Math.abs(latitude) > 90
                        || Math.abs(longitude) > 180
                    ) {
                        return null
                    }
                    const fill = SEVERITIES()[crash['Crash severity']]?.fill || SEVERITIES()[0].fill
                    return (
                        <Marker
                            key={`marker-${crash['Crash id']}`}
                            longitude={longitude}
                            latitude={latitude}
                            offset={OFFSET}
                            onClick={e => { e.originalEvent.stopPropagation(); setPopupInfo(crash) }}
                        >
                            <CustomPin
                                fill={fill}
                                scale={PINSCALE}
                                organization={!crash.merge_date && crash.organization}
                                organizations={organizations}
                            />
                        </Marker>
                    )
                })
                .filter(Boolean),
        [allCrashes, organizations]
    )

    const publicCrashesMarkers = useMemo(
        () =>
            publicCrashes?.map((crash, i) => {
                const fill = '#7C96FD'
                const longitude = +crash.longitude
                const latitude = +crash.latitude
                return (
                    <Marker
                        key={`public-crash-${i}`}
                        longitude={longitude}
                        latitude={latitude}
                        offset={OFFSET}
                        onClick={e => { e.originalEvent.stopPropagation(); setPopupPublicCrashInfo({ Longitude: longitude, Latitude: latitude, ...crash }) }}
                    >
                        <CustomPin fill={fill} scale={PINSCALE} organization={2} organizations={organizations} />
                    </Marker>
                )
            }),
        [publicCrashes, organizations]
    )

    const safetyIssuesMarkers = useMemo(
        () =>
            safetyIssuesList?.map((crash, i) => {
                const fill = '#470080'
                const longitude = crash.longitude
                const latitude = crash.latitude
                return (
                    <Marker
                        key={`safety-issue-${i}`}
                        longitude={longitude}
                        latitude={latitude}
                        offset={OFFSET}
                        onClick={e => { e.originalEvent.stopPropagation(); setPopupSafetyIssueInfo({ Longitude: longitude, Latitude: latitude, ...crash }) }}
                    >
                        <CustomPin fill={fill} scale={PINSCALE} organization={crash.sender} organizations={organizations} />
                    </Marker>
                )
            }),
        [safetyIssuesList, organizations]
    )

    const CrashPopup = ({ popupInfo }) => (
        <PopupComponent
            className="crash-map-detail-popup"
            offset={OFFSET.map(v => v * 1.5)}
            longitude={Number(popupInfo.Longitude)}
            latitude={Number(popupInfo.Latitude)}
            closeButton={false}
            closeOnClick={false}
            focusAfterOpen={false}
            maxWidth={isMobile ? '92vw' : '400px'}
            onClose={() => setPopupInfo(null)}
        >
            <CrashMapDetailPanel
                key={popupInfo['Crash id']}
                crash={popupInfo}
                organizations={organizations ?? []}
                configuration={init?.configuration}
                user={user}
                isMobile={isMobile}
                isDark={mode === 'dark'}
                onClose={() => setPopupInfo(null)}
                onViewEdit={(crash) => {
                    getCrash(crash['Crash id'], crash.organization, crash.user_id)
                    handleIsPositionSetState(true)
                    setPopupInfo(null)
                }}
            />
        </PopupComponent>
    )

    const PublicCrashPopup = ({ popupInfo }) => (
        <PopupComponent
            offset={OFFSET.map(v => v * 1.5)}
            longitude={Number(popupInfo.Longitude)}
            latitude={Number(popupInfo.Latitude)}
            focusAfterOpen={false}
            maxWidth={isMobile ? '92vw' : undefined}
            onClose={() => { setPopupInfo(null); setLayerPopupInfo(null); }}
        >
            <Box display='flex' flexDirection='column' alignItems='center'>
                <Typography variant='body2'><Trans>Report type: Public</Trans></Typography>
                <br />
                <Typography variant='body2'><Trans>Crash id:</Trans></Typography>
                <Typography fontWeight='bold' variant='body1'>{popupInfo.id}</Typography>
                <Button sx={{ textDecoration: 'underline', marginTop: 2, cursor: 'pointer', color: 'white' }}
                    onClick={() => { setModalInfo({ data: popupInfo, label: _(msg`crash details`) }) }}><Trans>View</Trans></Button>
            </Box>
        </PopupComponent>
    )

    const SafetyIssuePopup = ({ popupInfo }) => (
        <PopupComponent
            offset={OFFSET.map(v => v * 1.5)}
            longitude={Number(popupInfo.Longitude)}
            latitude={Number(popupInfo.Latitude)}
            focusAfterOpen={false}
            maxWidth={isMobile ? '92vw' : undefined}
            onClose={() => { setPopupInfo(null); setLayerPopupInfo(null); }}
        >
            <Box display='flex' flexDirection='column' alignItems='center'>
                <Typography sx={{ fontSize: 15 }}>{_(msg`Report type: `)}</Typography>
                <Typography fontWeight='bold' sx={{ fontSize: 14 }}><Trans>Safety Issue</Trans></Typography>
                <Typography sx={{ marginTop: 2, fontSize: 15 }} variant='body2'><Trans>Segnalation id:</Trans></Typography>
                <Typography fontWeight='bold' sx={{ fontSize: 14 }}>{popupInfo.id}</Typography>
                <Typography sx={{ marginTop: 2, fontSize: 15 }}><Trans>Description:</Trans></Typography>
                <Typography fontWeight='bold' sx={{ textAlign: 'center', fontSize: 14 }}>{popupInfo.description}</Typography>
                <Button sx={{ textDecoration: 'underline', marginTop: 2, cursor: 'pointer', color: 'white' }}
                    onClick={() => { setModalInfo({ data: popupInfo, label: _(msg`report details`) }) }}><Trans>View</Trans></Button>
            </Box>
        </PopupComponent>
    )

    const onClick = useCallback(e => {
        handleSetNewCrash({ isNew: true, Longitude: e.lngLat.lng, Latitude: e.lngLat.lat })
        handleCoordinates(e.lngLat)
        handleFormOpen(true)
        handlePositioningState(false)
        handleIsPositionSetState(true)
    }, [handlePositioningState, handleCoordinates, handleFormOpen, handleSetNewCrash, handleIsPositionSetState])

    const POLYGON_CHART_FIELD = { target: 'crash', fieldName: 'Crash severity' }

    const secondChartOptions = useMemo(() => {
        const regionFieldName = resolveStatsChartFieldName(
            init?.configuration,
            'crash',
            'State',
        )
        return [
        { label: _(msg`Crashes by year`), chartField: { reportType: 0, target: 'crash', fieldName: 'Crash date' }, groupBy: 'yyyy', groupByFormat: 'yyyy' },
        { label: _(msg`Crashes by month`), chartField: { reportType: 0, target: 'crash', fieldName: 'Crash date' }, groupBy: 'MM', groupByFormat: 'MM' },
        { label: _(msg`Crashes by day of week`), chartField: { reportType: 0, target: 'crash', fieldName: 'Crash date' }, groupBy: 'dayOfWeek' },
        { label: _(msg`Crashes by gender`), chartField: { reportType: 0, target: 'people', fieldName: 'Gender' } },
        { label: _(msg`Crashes by age group`), chartField: { reportType: 0, target: 'people', fieldName: 'Age group' } },
        { label: _(msg`Crashes by area type`), chartField: { reportType: 0, target: 'road', fieldName: 'Area type' } },
        { label: _(msg`Crashes by region`), chartField: { reportType: 0, target: 'crash', fieldName: regionFieldName } },
        { label: _(msg`Crashes by road user type`), chartField: { reportType: 0, target: 'people', fieldName: 'Road user type' } },
        { label: _(msg`Crashes by road function`), chartField: { reportType: 0, target: 'road', fieldName: 'Functional class - 1st road' } },
        { label: _(msg`Crashes by speed limit`), chartField: { reportType: 0, target: 'road', fieldName: 'Speed limit' } },
        { label: _(msg`Crashes by type`), chartField: { reportType: 0, target: 'crash', fieldName: 'Crash type' } },
        { label: _(msg`Alcohol involvement in crashes`), chartField: { reportType: 0, target: 'people', fieldName: 'Alcohol use' }, countMode: 'distinctCrashes' },
        { label: _(msg`Crashes by safety equipment use`), chartField: { reportType: 0, target: 'people', fieldName: 'Safety equipment use' }, countMode: 'distinctCrashes' },
    ].filter((opt) =>
        chartFieldExistsInStatsConfig(
            init?.configuration,
            opt.chartField.target,
            opt.chartField.fieldName,
        )
    )
    }, [_, init?.configuration])

    const normalizePolygonStatsToChartDataWithField = useCallback((responseData, chartOption) => {
        if (!chartOption?.chartField) return []
        const rawRows = extractChartRowsFromStatsResponse(responseData)
        const chart = {
            type: 'bar',
            payload: {
                chartField: chartOption.chartField,
                groupBy: chartOption.groupBy,
                countMode: chartOption.countMode,
            },
            groupByFormat: chartOption.groupByFormat ?? chartOption.groupBy,
        }
        return normalizeStatsRowsToChartData({
            rawRows,
            formModel: init?.configuration ?? {},
            chart,
            translate: _,
        }).map((item) => {
            let label = item.label
            if (label == null || String(label).trim() === '') label = _(msg`Other`)
            else if (String(label).trim() === '0') label = _(msg`Unspecified`)
            return { ...item, label }
        })
    }, [init, _])

    const polygonChartData = useMemo(() => {
        const rawRows = extractChartRowsFromStatsResponse(polygonStatsData)
        return normalizeStatsRowsToChartData({
            rawRows,
            formModel: init?.configuration ?? {},
            chart: {
                type: 'bar',
                payload: {
                    chartField: {
                        reportType: STATS_REPORT_TYPE_MERGED,
                        target: POLYGON_CHART_FIELD.target,
                        fieldName: POLYGON_CHART_FIELD.fieldName,
                    },
                },
            },
            translate: _,
        })
    }, [polygonStatsData, init?.configuration, _])

    const polygonChartDataSecond = useMemo(
        () => normalizePolygonStatsToChartDataWithField(polygonStatsDataSecond, selectedSecondChartOption),
        [polygonStatsDataSecond, selectedSecondChartOption, normalizePolygonStatsToChartDataWithField]
    )

    const barChartConfig = useMemo(() => ({
        type: polygonChartTypes[0],
        data: polygonChartData,
        yLabel: _(msg`Count`),
        ordinalColors: polygonMainOrdinalColors,
        colorMap: getDashboardColorMapForChartType(
            polygonChartTypes[0],
            statisticsMapGradientColors,
            polygonMainOrdinalColors,
        ),
    }), [polygonChartData, polygonChartTypes, polygonMainOrdinalColors, _])

    const pieChartConfig = useMemo(() => ({
        type: polygonChartTypes[1],
        data: polygonChartData,
        ordinalColors: polygonMainOrdinalColors,
        colorMap: getDashboardColorMapForChartType(
            polygonChartTypes[1],
            statisticsMapGradientColors,
            polygonMainOrdinalColors,
        ),
    }), [polygonChartData, polygonChartTypes, polygonMainOrdinalColors])

    const secondChartConfig = useMemo(() => ({
        type: polygonChartTypes[1],
        data: polygonChartDataSecond,
        ordinalColors: polygonSecondOrdinalColors,
        colorMap: getDashboardColorMapForChartType(
            polygonChartTypes[1],
            statisticsMapGradientColors,
            polygonSecondOrdinalColors,
        ),
    }), [polygonChartDataSecond, polygonChartTypes, polygonSecondOrdinalColors])

    const handlePolygonChartTypeChange = useCallback((event, index) => {
        setPolygonChartTypes(prev => ({ ...prev, [index]: event.target.value }))
    }, [])

    const buildPolygonStatsChartRequest = useCallback((chartField, options = {}) => {
        const base = buildStatsRequestPayload({
            chartField,
            fields: init.configuration,
            filters: [],
            groupBy: options.groupBy,
            countMode: options.countMode,
        })
        return enrichStatsRequestPayload(base, {
            user: init?.user,
            organizations: init?.organizations ?? [],
            reportType: chartField.reportType ?? STATS_REPORT_TYPE_MERGED,
        })
    }, [init])

    const handlePolygonComplete = async (points) => {
        const pointsPayload = points.map(([lng, lat]) => ({ longitude: lng, latitude: lat }))
        lastPolygonPointsRef.current = pointsPayload

        const payload = {
            chartRequest: buildPolygonStatsChartRequest({
                reportType: STATS_REPORT_TYPE_MERGED,
                target: 'crash',
                fieldName: 'Crash severity',
            }),
            points: pointsPayload,
        }

        setPolygonStatsLoading(true)
        try {
            const res = await request(getCrashesInPolygonUrl, 'POST', payload, { Authorization: `Bearer ${token}` })

            if (res?.responseAnyError) return
            setPolygonStatsData(res.responseData ?? null)
            setStatsPanelOpen(true)
            onStatsPanelOpen?.()
        } finally {
            setPolygonStatsLoading(false)
        }
    }

    const handleSecondChartSearch = useCallback(async (chartOption) => {
        const points = lastPolygonPointsRef.current
        if (!points || !chartOption) return
        const chartField = chartOption.chartField ?? chartOption
        setSelectedSecondChartOption(chartOption)
        setPolygonStatsLoading(true)
        try {
            const chartRequest = buildPolygonStatsChartRequest(chartField, {
                groupBy: chartOption.groupBy ?? chartOption.groupByFormat,
                countMode: chartOption.countMode,
            })
            const payload = {
                chartRequest,
                points,
            }
            const res = await request(getCrashesInPolygonUrl, 'POST', payload, { Authorization: `Bearer ${token}` })
            if (res?.responseAnyError) return
            setPolygonStatsDataSecond(res.responseData ?? null)
        } finally {
            setPolygonStatsLoading(false)
        }
    }, [request, token, buildPolygonStatsChartRequest])

    return (
        <Box
            height={isMobile ? `calc(100vh - ${barHeight * 2}px)` : `calc(100vh - ${barHeight}px)`}
            sx={{
                position: 'relative',
                ...(!isMobile && isFormOpen && { marginInlineStart: `${formDrawerWidth}px` }),
                ...(!isMobile && (isHistoryOpen || statsPanelOpen) && { marginInlineEnd: isHistoryOpen ? `${DESKTOP_HISTORY_DRAWER_WIDTH_PX}px` : `${STATS_DRAWER_WIDTH}px` })
            }}
        >
            <PolygonStatsBackdrop open={polygonStatsLoading} />
            <PolygonStatsResults
                isMobile={isMobile}
                open={statsPanelOpen}
                onClose={() => {
                    setStatsPanelOpen(false)
                    setPolygonStatsData(null)
                    setPolygonStatsDataSecond(null)
                    setSelectedSecondChartOption(null)
                    onStatsPanelClose?.()
                }}
                chartData={polygonChartData}
                barChartConfig={barChartConfig}
                pieChartConfig={pieChartConfig}
                onChartTypeChange={handlePolygonChartTypeChange}
                secondChartOptions={secondChartOptions}
                onSecondChartSearch={handleSecondChartSearch}
                secondChartData={polygonChartDataSecond}
                secondChartConfig={secondChartConfig}
                severitySummary={polygonStatsData?.severitySummary ?? null}
                width={STATS_DRAWER_WIDTH}
            />
            <Snackbar
                open={polygonSnackbarOpen}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
                message={_(msg`Click on the map to define the area for statistics`)}
                TransitionComponent={Slide}
                sx={{
                    '& .MuiSnackbarContent-root': {
                        backgroundColor: theme.palette.mode === 'dark'
                            ? theme.palette.background.paper
                            : theme.palette.primary.contrastText,
                        color: theme.palette.mode === 'dark'
                            ? theme.palette.text.primary
                            : theme.palette.newColors.gray,
                        fontWeight: 'bold',
                        border: theme.palette.mode === 'dark' ? `1px solid ${theme.palette.divider}` : undefined,
                        boxShadow: theme.palette.mode === 'dark' ? 2 : undefined,
                    },
                }}
            />
            <Map
                style={{ width: '100%', height: '100%', cursor: isPositioning ? 'none' : 'grab' }}
                mapStyle={`https://api.maptiler.com/maps/${mapStyle}/style.json?key=${mapKey}`}
                attributionControl={false}
                onLoad={() => {
                    // Trigger geolocate solo se shouldTriggerGeoControl è true
                    if (shouldTriggerGeoControl?.current !== false) {
                        geoControlRef.current?.trigger()
                    }
                }}
                onMouseMove={e => {
                    if (!isMobile && isPositioning) {
                        setSelectorTop(e.point.y)
                        setSelectorLeft(isFormOpen ? e.point.x - formDrawerWidth : e.point.x)
                    }
                }}
                cursor={!isMobile && isPositioning ? 'none' : 'Grab'}
                onClick={e => { if (isPositioning) onClick(e) }}
                ref={mapRef}
            >
                <CrashMapZoomSync setMapZoom={setMapZoom} />
                {typeof onMapBoundsChange === 'function' && (
                    <MapBoundsReporter onMapBoundsChange={onMapBoundsChange} />
                )}
                {layersVisibility?.riskMap === 'visible' && (
                    <RoadRiskClickHandler
                        visible
                        onFeatureClick={handleRoadRiskFeatureClick}
                    />
                )}
                {(layersVisibility?.analysis === 'visible' || layersVisibility?.riskMap === 'visible')
                  && roadNetworkUnderlayEnabled && (
                    <RoadNetworkUnderlayClickHandler
                        visible
                        onFeatureClick={handleRoadNetworkFeatureClick}
                    />
                )}
                <CommunityMapLayerClickHandler
                    layersList={layersList}
                    enabled={!isPositioning}
                    onFeatureClick={handleCommunityLayerFeatureClick}
                />
                <CommunityMapLayerFocusHandler
                    layersList={layersList}
                    focusMapLayerRef={focusMapLayerRef}
                    mapRef={mapRef}
                />
                {/* <FlyToCrash /> */}
                {/* CLUSTER SOURCE + LAYERS */}
                {showCrashes && (
                    <>
                        <Source
                            id="crash-clusters"
                            type="geojson"
                            data={crashesGeoJSON}
                            cluster={true}
                            clusterRadius={50}
                            clusterMaxZoom={CRASH_CLUSTER_MAX_ZOOM}
                        >
                            <Layer
                                id="cluster-layer"
                                type="circle"
                                filter={["has", "point_count"]}
                                paint={{
                                    "circle-color": ["step", ["get", "point_count"], "#51bbd6", 10, "#f1f075", 30, "#f28cb1"],
                                    "circle-radius": ["step", ["get", "point_count"], 18, 10, 24, 30, 30],
                                    "circle-opacity": 0.85
                                }}
                            />
                            <Layer
                                id="cluster-count"
                                type="symbol"
                                filter={["has", "point_count"]}
                                layout={{
                                    "text-field": "{point_count_abbreviated}",
                                    "text-font": ["Open Sans Bold", "Arial Unicode MS Bold"],
                                    "text-size": 12
                                }}
                                paint={{ "text-color": "#000000" }}
                            />
                            <Layer
                                id="cluster-click"
                                type="circle"
                                filter={["has", "point_count"]}
                                paint={{ "circle-radius": 40, "circle-opacity": 0 }}
                            />
                            {/*
                              Punti unclustered: cerchi WebGL (nessun lag React) così non c’è “buco” tra cluster e pin DOM.
                              Opacity → 0 mentre si avvicina la soglia CustomPin (zoom > clusterMaxZoom).
                            */}
                            <Layer
                                id="unclustered-crash-circles"
                                type="circle"
                                filter={["!", ["has", "point_count"]]}
                                paint={{
                                    "circle-color": UNCLUSTERED_SEVERITY_CIRCLE_COLOR,
                                    "circle-radius": 7,
                                    "circle-stroke-width": 1,
                                    "circle-stroke-color": "#ffffff",
                                    "circle-opacity": [
                                        "interpolate",
                                        ["linear"],
                                        ["zoom"],
                                        CRASH_CLUSTER_MAX_ZOOM,
                                        0.92,
                                        CRASH_CLUSTER_MAX_ZOOM + 0.35,
                                        0
                                    ]
                                }}
                            />
                        </Source>
                        <ClusterClickHandler mapRef={mapRef} />
                    </>
                )}
                <Box
                    className="maplibregl-ctrl maplibregl-ctrl-group"
                    sx={{
                        position: 'absolute',
                        right: 10,
                        bottom: MAP_CUSTOM_TOOLBAR_BOTTOM_PX,
                        zIndex: theme.zIndex.drawer - 1,
                    }}
                >
                    {canDrawPolygon && (
                        <PolygonDrawerButton
                            onPolygonComplete={handlePolygonComplete}
                            onDrawingStart={() => { setPolygonSnackbarOpen(true); onDrawingStart?.() }}
                            onDrawingEnd={() => { setPolygonSnackbarOpen(false); onDrawingEnd?.() }}
                        />
                    )}
                    <CommunityMapButton setCommunitySwitches={setCommunitySwitches} communitySwitches={communitySwitches} />
                    <LayerMapButton
                        isPositionSet={isPositionSet}
                        isPositioning={isPositioning}
                        isFormOpen={isFormOpen}
                        setLayersList={setLayersList}
                        layersList={layersList}
                        onFocusMapLayer={(layerId) => focusMapLayerRef.current?.(layerId)}
                        canReorderMapLayers={canReorderMapLayers}
                        onMoveLayerUp={onMoveLayerUp}
                        onMoveLayerDown={onMoveLayerDown}
                        isMapLayerReordering={isMapLayerReordering}
                    />
                    <StylesControl handleSetStyle={handleSetStyle} position='bottom-right' inToolbar />
                </Box>
                <NavigationControl position='bottom-right' />
                <GeolocateControl
                    ref={geoControlRef}
                    position='bottom-right'
                    showAccuracyCircle={isMobile}
                    positionOptions={{ enableHighAccuracy: true }}
                    onGeolocate={({ coords: { latitude, longitude } }) => {
                        handleOwnPosCoords({ lat: latitude, lng: longitude })
                    }}
                />
                <ScaleControl />
                <Copyright bgcolor='hsla(0, 0%, 100%, .5)' left='50%' bottom={0} />
                <Slide in={isPositioning} timeout={transitionDuration} unmountOnExit>
                    <Box
                        position='absolute'
                        top={isMobile ? '1rem' : '3rem'}
                        width='100%'
                        display='flex'
                        flexDirection='column'
                        alignItems='center'
                        zIndex={isMobile ? theme.zIndex.drawer - 1 : 99999}
                    >
                        <Chip label={_(msg`Please select the new crash location`)} color='error' />
                    </Box>
                </Slide>
                {isPositioning && (
                    <NewCrashSelector
                        selectorTop={selectorTop}
                        selectorLeft={selectorLeft}
                        navDrawerWidthPx={navDrawerWidthPx}
                    />
                )}
                {newCrash && (
                    <Marker
                        longitude={newCrash.Longitude}
                        latitude={newCrash.Latitude}
                        draggable={!newCrash.merge_date}
                        onDragEnd={e => {
                            const lngLat = e.target.getLngLat()
                            handleSetNewCrash({ ...newCrash, Longitude: lngLat.lng, Latitude: lngLat.lat })
                            handleCoordinates(lngLat)
                        }}
                    >
                        <CustomPin />
                    </Marker>
                )}

                {/* CustomPin quando zoom > clusterMaxZoom; CrashMapZoomSync tiene mapZoom allineato */}
                {showCrashes && shouldShowCrashPins(mapZoom) && allCrashesMarkers}
                {showPublicCrashes && publicCrashesMarkers}
                {showSafetyIssues && safetyIssuesMarkers}
                {popupInfo && <CrashPopup popupInfo={popupInfo} />}
                {popupPublicCrashInfo && <PublicCrashPopup popupInfo={popupPublicCrashInfo} />}
                {popupSafetyIssueInfo && <SafetyIssuePopup popupInfo={popupSafetyIssueInfo} />}
                {modalinfo && <ModalVisMarkerInfo data={modalinfo.data} type={modalinfo.label} isMobile={isMobile} setData={setModalInfo} />}
                {!isMobile && <GeneralInfo crashesSummary={crashesSummary} />}
                {heatMapData && (
                    <Source key='heatmap' type='geojson' data={heatMapData}>
                        <Layer key='heatmap' {...heatmapLayer} layout={{ visibility: layersVisibility?.heatmap }} />
                    </Source>
                )}
                {roadNetworkMapData && (
                    <Source id='road-network-underlay' type='geojson' data={roadNetworkMapData}>
                        <Layer
                            {...roadNetworkUnderlayLayer}
                            layout={{
                                ...roadNetworkUnderlayLayer.layout,
                                visibility: layersVisibility?.analysis === 'visible' ? 'visible' : 'none',
                            }}
                        />
                        <Layer
                            {...roadNetworkUnderlayHitLayer}
                            layout={{
                              visibility: layersVisibility?.analysis === 'visible' ? 'visible' : 'none',
                            }}
                        />
                    </Source>
                )}
                {roadRiskMapData && (
                    <Source id='road-risk-map' type='geojson' data={roadRiskMapData}>
                        <Layer
                            {...roadRiskLayerStyle}
                            layout={{
                                ...roadRiskLayerStyle.layout,
                                visibility: layersVisibility?.riskMap ?? 'none',
                            }}
                        />
                        <Layer
                            {...roadRiskMapHitLayer}
                            layout={{ visibility: layersVisibility?.riskMap ?? 'none' }}
                        />
                    </Source>
                )}
                {layerPopupInfo && (
                    <CommunityMapLayerPopup
                        popupInfo={layerPopupInfo}
                        isMobile={isMobile}
                        onClose={() => setLayerPopupInfo(null)}
                    />
                )}
                {riskMapPopupInfo && (
                    <PopupComponent
                        longitude={riskMapPopupInfo.longitude}
                        latitude={riskMapPopupInfo.latitude}
                        anchor="bottom"
                        onClose={() => setRiskMapPopupInfo(null)}
                        closeOnClick={false}
                        focusAfterOpen={false}
                        maxWidth={isMobile ? '92vw' : '280px'}
                    >
                        <Stack spacing={0.5} sx={{ p: 0.5 }}>
                            {riskMapPopupInfo.kind === 'network' ? (
                                <>
                                    <Typography variant="subtitle2">
                                        {riskMapPopupInfo.properties.segmentId}
                                        {' — '}
                                        {riskMapPopupInfo.properties.name
                                            || riskMapPopupInfo.properties.ref
                                            || _(msg`Road segment`)}
                                    </Typography>
                                    <Typography variant="body2">
                                        <Trans>Length</Trans>: {Number(riskMapPopupInfo.properties.lengthM ?? 0).toLocaleString(undefined, { maximumFractionDigits: 0 })} m
                                    </Typography>
                                </>
                            ) : (
                                <>
                                    <Typography variant="subtitle2">
                                        {riskMapPopupInfo.properties.segmentId != null
                                            ? `${riskMapPopupInfo.properties.segmentId} — `
                                            : ''}
                                        {riskMapPopupInfo.properties.name
                                            || riskMapPopupInfo.properties.ref
                                            || _(msg`Road segment`)}
                                    </Typography>
                                    {riskMapPopupInfo.properties.fclass && (
                                        <Typography variant="caption" color="text.secondary">
                                            {riskMapPopupInfo.properties.fclass}
                                        </Typography>
                                    )}
                                    <Typography variant="body2">
                                        <Trans>Risk / km</Trans>: {formatRiskScore(riskMapPopupInfo.properties.riskScore)}
                                    </Typography>
                                    <Typography variant="body2">
                                        <Trans>Crashes</Trans>: {riskMapPopupInfo.properties.crashCount ?? 0}
                                    </Typography>
                                    <Typography variant="body2">
                                        <Trans>Length</Trans>: {Number(riskMapPopupInfo.properties.lengthM ?? 0).toLocaleString(undefined, { maximumFractionDigits: 0 })} m
                                    </Typography>
                                </>
                            )}
                        </Stack>
                    </PopupComponent>
                )}
                {layersList?.filter((layer) => layer.isOpen).map((layer) => (
                    <CommunityMapLayer key={layer.id} layer={layer} />
                ))}
            </Map>
            <Box
                sx={{
                    position: 'absolute',
                    left: 10,
                    top: 10,
                    zIndex: theme.zIndex.drawer - 1,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'flex-start',
                    gap: 1,
                    maxWidth: 'calc(100% - 20px)',
                    pointerEvents: 'none',
                    '& > *': { pointerEvents: 'auto' },
                }}
            >
                <MapYearFilterToolbar
                    availableYears={riskMapYears}
                    selectedYears={mapFilterYears}
                    onSelectedYearsChange={onMapFilterYearsChange}
                    afterFilterButton={
                        analysisToolsAvailable ? (
                            <RoadRiskMapToolbarToggle
                                open={analysisPanelOpen}
                                onToggle={() => setAnalysisPanelOpen((prev) => !prev)}
                                active={heatmapLayerOn || riskMapLayerOn}
                            />
                        ) : null
                    }
                />
                <RoadRiskMapToolbar
                    visible={analysisPanelOpen && analysisToolsAvailable}
                    settings={roadRiskMapMetadata?.settings}
                    roadRiskMapData={roadRiskMapData}
                    previousScoresBySegmentId={roadRiskMapPrevScores}
                    loading={riskMapLoading}
                    onRefresh={onRiskMapRefresh}
                    riskMapYear={riskMapYear}
                    analysisYearLocked={riskMapYear == null}
                    onFocusRiskSegment={handleFocusRiskSegmentFromTopTen}
                    roadNetworkUnderlayEnabled={roadNetworkUnderlayEnabled}
                    onRoadNetworkUnderlayEnabledChange={onRoadNetworkUnderlayEnabledChange}
                    mapZoom={mapZoom}
                    onDemoSeed={onRiskMapDemoSeed}
                    demoSeedLoading={riskMapDemoSeedLoading}
                    heatmapVisible={heatmapLayerOn}
                    onHeatmapVisibleChange={onHeatmapLayerOnChange}
                    riskMapVisible={riskMapLayerOn}
                    onRiskMapVisibleChange={onRiskMapLayerOnChange}
                    onCollapse={() => setAnalysisPanelOpen(false)}
                />
            </Box>
            <RoadRiskMapLegend
                visible={layersVisibility?.riskMap === 'visible'}
                maxRiskScore={roadRiskMapMetadata?.maxRiskScore ?? 0}
            />

        </Box>
    )
}
